import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import HttpRoutingService from "../../shared/services/httpRoutingService"
import { API } from "../../shared/apiRoutes"
import { OptionJsonType, addEditOptionJsonType } from "../../components/products/options/optionModule"

interface giftCardNameDuplicatePayload {
    value: String,
    columnName: String,
    tableName: String,
    storeId: boolean,
    id: number | null
}

export type DuplicateJsonType = {
    result:
    {
        alreadyExists: boolean
    },
    success: Boolean
}
export type serviceType = {
    loading: boolean,
    error: string | null
}
export type optionListPayload =
    {
        offset: number,
        limit: number,
        searchText: string | null,
        sortOrder?: string,
        filterData: string | null
    }
export type deleteOptionJson =
    {
        "optionsVariants": number[],
        "code": string,
        "message": string,
        "success": boolean
    }
export type optionPostPayload =
    {
        "name": string,
        "description": string | null,
        "showColors": boolean,
        "optionValues": {
            id: number | null,
            optionValue: string,
            sortOrder: number | null,
            color: string
        }[],
        "medium": string,
        "removeId": number[]
    }
export type optionPostResponse =
    {
        optionsVariants: {
            "optionsId": string,
            "createdAt": string,
            "modifiedAt": string,
            "id": number,
            "name": string,
            "description": string | null,
            "showColors": boolean,
            "storeId": string,
            "lastModifiedBy": number,
            "isActive": boolean,
            "sortOrder": number | null,
            "isDeleted": boolean,
            "importId": number | null
        } | number[],
        "code": string,
        "message": string,
        "success": boolean
    }
export type optionSortPayload =
    {
        optionValueData: { optionValue: string | undefined, optionValueId: number, sortOrder: number }[]
    }
export type optionSortResponse =
    {
        "sortOrder": boolean,
        "code": string,
        "message": string,
        "success": boolean
    }
export type allOptionSortPayload =
    {
        optionData: { id: number, sortOrder: number }[]
    }


interface initialState {
    getPosOptionList: serviceType & { value: OptionJsonType | null },
    addEditOptionJsonType: serviceType & { value: addEditOptionJsonType | null },
    getOneDeletePosOptionList: serviceType & { value: deleteOptionJson | null },
    getOnePostOptionData: serviceType & { value: optionPostResponse | null },
    getOneUpdateOptionData: serviceType & { value: optionPostResponse | null },
    getOneSortOptionData: serviceType & { value: optionSortResponse | null },
    getAllSortOptionData: serviceType & { value: optionSortResponse | null },
    optionCache: Record<string, OptionJsonType>
    getOnePostDuplicateCheck: serviceType & { value: DuplicateJsonType | null }
}

const intialState: initialState =
{
    getPosOptionList: { loading: false, value: null, error: '' },
    addEditOptionJsonType: { loading: false, value: null, error: '' },
    getOneDeletePosOptionList: { loading: false, value: null, error: '' },
    getOnePostOptionData: { loading: false, value: null, error: '' },
    getOneUpdateOptionData: { loading: false, value: null, error: '' },
    getOneSortOptionData: { loading: false, value: null, error: '' },
    getAllSortOptionData: { loading: false, value: null, error: '' },
    getOnePostDuplicateCheck: { loading: false, value: null, error: '' },
    optionCache: {}
}

export const getAllPosOptionList = createAsyncThunk(
    "option/getPosOptionList",
    async (
        { storeId, data, refresh }: { storeId: number, data: optionListPayload | string, refresh?: boolean },
        { getState }
    ) => {
        const cacheKey = JSON.stringify({
            storeId,
            data: typeof data === 'object' ? { ...(data as any), refresh: undefined } : data
        });
        const state = getState() as any;
        const bypassCache = refresh || (data as any)?.refresh;
        if (!bypassCache && state.optionSlice?.optionCache?.[cacheKey]) {
            return state.optionSlice.optionCache[cacheKey];
        }
        const url = HttpRoutingService?.replaceUrl(API?.getPosOptionList, { storeId: storeId });
        return HttpRoutingService?.getMethod<OptionJsonType>(url, data);
    }
)

export const getOnePosOptionList = createAsyncThunk('option/getOnePosOptionList', ({ storeId, selectedId }: { storeId: number, selectedId: number }) => {
    const url = HttpRoutingService.replaceUrl(API.getOnePosOptionList, { storeId: storeId, selectedId: selectedId })
    return HttpRoutingService.getMethod<addEditOptionJsonType>(url)
})

export const getOneDeletePosOptionList = createAsyncThunk('option/getOneDeletePosOptionList', ({ storeId, deleteId }: { storeId: number, deleteId: number }) => {
    const url = HttpRoutingService.replaceUrl(API.getOneDeletePosOptionList, { storeId: storeId, deleteId: deleteId })
    return HttpRoutingService.deleteMethod<deleteOptionJson>(url)
})

export const getOnePostOptionData = createAsyncThunk('option/getOnePostOptionData', ({ storeId, data }: { storeId: number, data: optionPostPayload }) => {
    const url = HttpRoutingService.replaceUrl(API.getOnePostOptionData, { storeId: storeId })
    return HttpRoutingService.postMethod<optionPostResponse>(url, data)
})

export const getOneUpdateOptionData = createAsyncThunk('option/getOneUpdateOptionData', ({ storeId, updateId, data }: { storeId: number, updateId: number, data: optionPostPayload }) => {
    const url = HttpRoutingService.replaceUrl(API.getOneUpdateOptionData, { storeId: storeId, updateId: updateId })
    return HttpRoutingService.putMethod<optionPostResponse>(url, data)
})

export const getOneSortOptionData = createAsyncThunk('option/getOneSortOptionData', ({ storeId, updateId, data }: { storeId: number, updateId: number, data: optionSortPayload }) => {
    const url = HttpRoutingService.replaceUrl(API.getOneSortOptionData, { storeId: storeId, updateId: updateId })
    return HttpRoutingService.putMethod<optionSortResponse>(url, data)
})

export const getAllSortOptionData = createAsyncThunk('option/getAllSortOptionData', ({ storeId, data }: { storeId: number, data: allOptionSortPayload }) => {
    const url = HttpRoutingService.replaceUrl(API.getAllSortOptionData, { storeId: storeId })
    return HttpRoutingService.putMethod<optionSortResponse>(url, data)
})

export const getOnePostDuplicateCheck = createAsyncThunk('giftCard/getOnePostDuplicateCheck', ({ storeId, data }: { storeId: number, data: giftCardNameDuplicatePayload }) => {
    const url = HttpRoutingService.replaceUrl(API.getOnePostDuplicateCheck, { storeId: storeId })
    return HttpRoutingService.postMethod<DuplicateJsonType>(url, data)
})

const optionSlice = createSlice({
    name: 'option',
    initialState: intialState,
    reducers: {
        clearOptionCache: (state) => {
            state.optionCache = {};
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getAllPosOptionList.pending, (state) => {
                state.getPosOptionList.loading = true
            })
            .addCase(getAllPosOptionList.fulfilled, (state, action) => {
                state.getPosOptionList.loading = false
                state.getPosOptionList.value = action?.payload
                const cacheKey = JSON.stringify({
                    storeId: action.meta.arg.storeId,
                    data: typeof action.meta.arg.data === 'object'
                        ? { ...(action.meta.arg.data as any), refresh: undefined }
                        : action.meta.arg.data
                });
                if (!state.optionCache) {
                    state.optionCache = {};
                }
                if (action.payload) {
                    state.optionCache[cacheKey] = action.payload;
                }
            })
            .addCase(getAllPosOptionList.rejected, (state, action) => {
                state.getPosOptionList.loading = false
                state.getPosOptionList.error = action.error.message as string
            })

            .addCase(getOnePosOptionList.pending, (state) => {
                state.addEditOptionJsonType.loading = true
            })
            .addCase(getOnePosOptionList.fulfilled, (state, action) => {
                state.addEditOptionJsonType.loading = false
                state.addEditOptionJsonType.value = action.payload
            })
            .addCase(getOnePosOptionList.rejected, (state, action) => {
                state.addEditOptionJsonType.loading = false
                state.addEditOptionJsonType.error = action.error.message as string

            })

            .addCase(getOnePostDuplicateCheck.pending, (state) => {
                state.getOnePostDuplicateCheck.loading = true
            })
            .addCase(getOnePostDuplicateCheck.fulfilled, (state, action) => {
                state.getOnePostDuplicateCheck.loading = false
                state.getOnePostDuplicateCheck.value = action.payload
            })
            .addCase(getOnePostDuplicateCheck.rejected, (state, action) => {
                state.getOnePostDuplicateCheck.loading = false
                state.getOnePostDuplicateCheck.error = action.error.message as string
            })

            .addCase(getOneDeletePosOptionList.pending, (state) => {
                state.getOneDeletePosOptionList.loading = true
            })
            .addCase(getOneDeletePosOptionList.fulfilled, (state, action) => {
                state.getOneDeletePosOptionList.loading = false
                state.getOneDeletePosOptionList.value = action.payload
                state.optionCache = {}
            })
            .addCase(getOneDeletePosOptionList.rejected, (state, action) => {
                state.getOneDeletePosOptionList.loading = false
                state.getOneDeletePosOptionList.error = action.error.message as string
            })

            .addCase(getOnePostOptionData.pending, (state) => {
                state.getOnePostOptionData.loading = true
            })
            .addCase(getOnePostOptionData.fulfilled, (state, action) => {
                state.getOnePostOptionData.loading = false
                state.getOnePostOptionData.value = action.payload
                state.optionCache = {}
            })
            .addCase(getOnePostOptionData.rejected, (state, action) => {
                state.getOnePostOptionData.loading = false
                state.getOnePostOptionData.error = action.error.message as string
            })

            .addCase(getOneUpdateOptionData.pending, (state, action) => {
                state.getOneUpdateOptionData.loading = true
            })
            .addCase(getOneUpdateOptionData.fulfilled, (state, action) => {
                state.getOneUpdateOptionData.loading = false
                state.getOneUpdateOptionData.value = action.payload
                state.optionCache = {}
            })
            .addCase(getOneUpdateOptionData.rejected, (state, action) => {
                state.getOneUpdateOptionData.loading = false
                state.getOneUpdateOptionData.error = action.error.message as string
            })

            .addCase(getOneSortOptionData.pending, (state) => {
                state.getOneSortOptionData.loading = true
            })
            .addCase(getOneSortOptionData.fulfilled, (state, action) => {
                state.getOneSortOptionData.loading = false
                state.getOneSortOptionData.value = action.payload
                state.optionCache = {}
            })
            .addCase(getOneSortOptionData.rejected, (state, action) => {
                state.getOneSortOptionData.loading = false
                state.getOneSortOptionData.error = action.error.message as string
            })

            .addCase(getAllSortOptionData.pending, (state) => {
                state.getAllSortOptionData.loading = true
            })
            .addCase(getAllSortOptionData.fulfilled, (state, action) => {
                state.getAllSortOptionData.loading = false
                state.getAllSortOptionData.value = action.payload
                state.optionCache = {}
            })
            .addCase(getAllSortOptionData.rejected, (state, action) => {
                state.getAllSortOptionData.loading = false
                state.getAllSortOptionData.error = action.error.message as string
            })
    }
})

export const { clearOptionCache } = optionSlice.actions;
export default optionSlice.reducer;





